import React, { FC, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useDispatch, useSelector } from 'react-redux';

import {
  FolderManagerActionTypes,
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
  FolderManagerItemTypes,
  FolderManagerMimeTypes,
} from '../../../interfaces';

import { getActionsForItemType } from '../../../helpers';
import { usePresentationBuildPolling, usePresentationDownload } from '../../../hooks';
import { useGetPresentationQuery } from '../../../services';
import CircularLoader from '../../../shared/components/CircularLoader';
import { useDeepCompareMemo } from '@clatter/platform';
import { addNotification, authSelectors, pinItem, unpinItem, pinnedItemsSelectors } from '../../../store';
import { SlideThemePreviewDialog } from './SlideThemePreviewDialog';

const DropdownPanel = styled.div<{ $top: number; $right: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  right: ${({ $right }) => $right}px;
  z-index: 1400;
  background: ${({ theme }) => theme?.palette?.cardBackground ?? '#ffffff'};
  border: 1px solid ${({ theme }) => theme?.palette?.cardBorder ?? '#e5e7eb'};
  border-radius: ${({ theme }) => theme?.radius?.small ?? '0.57rem'};
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 160px;
`;

const DropdownItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 8px 14px;
  font-size: ${({ theme }) => theme?.fontSize?.sm ?? '0.75rem'};
  font-weight: 500;
  color: ${({ $danger, theme }) => ($danger ? (theme?.palette?.danger ?? '#dc2626') : (theme?.palette?.textMedium ?? '#374151'))};
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.1s;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${({ $danger, theme }) => ($danger ? (theme?.palette?.dangerDimmed ?? '#fef2f2') : (theme?.palette?.cardSurface ?? '#f9fafb'))};
  }
`;


interface DropdownMenuProps {
  anchorEl: null | HTMLElement;
  open: boolean;
  slug: string | undefined;
  isLoading: boolean | undefined;
  item: FolderManagerItem;
  actions?: IFolderManagerActionsItem[];
  selectedItems: FolderManagerItem[] | undefined;
  onClose: (event: React.MouseEvent<HTMLElement>) => void;
  onAction: (action: any) => void;
  itemActions: any;
  isPresentationBuilding?: boolean;
  isDownloadDisabled?: boolean;
  isDownloading?: boolean;
  onPresentationDownload?: () => void;
  onPreviewThemes?: () => void;
  isPinned?: boolean;
  onPinToggle?: () => void;
}

interface IFolderManagerItemActions {
  item: FolderManagerItem;
  selectedItems?: FolderManagerItem[];
  isLoading?: boolean;
  isTableView?: boolean;
  actions?: IFolderManagerActionsItem[];
  onAction?: any;
  isMulti?: boolean;
  viewStateKey?: string;
  triggerVariant?: 'dark';
}

interface IGetActionByName {
  slug: string;
  isLoading?: boolean;
  onAction: (data: any) => void;
  onClose: (event: any) => void;
  item: FolderManagerItem;
  selectedItems?: FolderManagerItem[];
  actionName: FolderManagerItemActionNames;
  isPresentationBuilding?: boolean;
  isDownloadDisabled?: boolean;
  isDownloading?: boolean;
  onPresentationDownload?: () => void;
  onPreviewThemes?: () => void;
  isPinned?: boolean;
  onPinToggle?: () => void;
}

const StyledActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
`;

const TriggerButton = styled.button<{ disabled?: boolean; $dark?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $dark }) => ($dark ? 'rgba(0,0,0,0.35)' : theme.palette.white)};
  backdrop-filter: ${({ $dark }) => ($dark ? 'blur(4px)' : 'none')};
  border: ${({ $dark }) => ($dark ? '1px solid rgba(255,255,255,0.2)' : 'none')};
  border-radius: ${({ $dark, theme }) => ($dark ? (theme?.radius?.xSmall ?? '0.30rem') : '5px')};
  padding: ${({ $dark }) => ($dark ? '4px 5px' : '3px 4px')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${({ $dark, theme }) => ($dark ? 'white' : (theme?.palette?.textSubtle ?? '#64748b'))};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  transition: background 0.12s, color 0.12s;
  line-height: 1;

  &:hover:not(:disabled) {
    background: ${({ $dark, theme }) => ($dark ? 'rgba(0,0,0,0.55)' : (theme?.palette?.iconButtonHoverBackground ?? '#f3f4f6'))};
    color: ${({ $dark, theme }) => ($dark ? 'white' : (theme?.palette?.textStrong ?? '#1e293b'))};
  }
`;

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  anchorEl,
  open,
  onClose,
  itemActions,
  item,
  slug,
  actions,
  onAction,
  isLoading,
  selectedItems,
  isPresentationBuilding,
  isDownloadDisabled,
  isDownloading,
  onPresentationDownload,
  onPreviewThemes,
  isPinned,
  onPinToggle,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !anchorEl) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || anchorEl.contains(target)) return;
      onClose({ stopPropagation: () => {}, preventDefault: () => {} } as any);
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose({ stopPropagation: () => {}, preventDefault: () => {} } as any);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open, anchorEl, onClose]);

  if (!open || !anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  const top = rect.bottom + 6;
  const right = window.innerWidth - rect.right;

  return ReactDOM.createPortal(
    <DropdownPanel ref={panelRef} $top={top} $right={right}>
      {(actions || itemActions).map((action: any) =>
        getActionByName({
          item,
          isLoading,
          selectedItems,
          slug: slug || '',
          actionName: action.name,
          onClose,
          onAction,
          isPresentationBuilding,
          isDownloadDisabled,
          isDownloading,
          onPresentationDownload,
          onPreviewThemes,
          isPinned,
          onPinToggle,
        }),
      )}
    </DropdownPanel>,
    document.body,
  );
};

const getPinTypeLabel = (type: string): string => {
  switch (type) {
    case FolderManagerItemTypes.WORKSPACE: return 'Workspace';
    case FolderManagerItemTypes.FOLDER: return 'Folder';
    case FolderManagerItemTypes.PRESENTATION: return 'Presentation';
    case FolderManagerItemTypes.COLLECTION: return 'Collection';
    case FolderManagerItemTypes.SLIDE_REPOSITORY: return 'Slidespace';
    case FolderManagerItemTypes.ASSET: return 'File';
    default: return 'Item';
  }
};

const themeActions = [
  { name: FolderManagerItemActionNames.RENAME, type: FolderManagerItemActionTypes.SINGLE },
  { name: FolderManagerItemActionNames.DOWNLOAD, type: FolderManagerItemActionTypes.MULTI },
  { name: FolderManagerItemActionNames.MANAGE_THEME, type: FolderManagerItemActionTypes.SINGLE },
  { name: FolderManagerItemActionNames.MANAGE_VERSIONS, type: FolderManagerItemActionTypes.SINGLE },
  { name: FolderManagerItemActionNames.SET_AS_DEFAULT, type: FolderManagerItemActionTypes.SINGLE },
  { name: FolderManagerItemActionNames.RESTORE, type: FolderManagerItemActionTypes.MULTI },
  { name: FolderManagerItemActionNames.DELETE, type: FolderManagerItemActionTypes.MULTI },
];

const getActionByName = ({
  actionName,
  slug,
  onAction,
  item,
  selectedItems,
  onClose,
  isPresentationBuilding,
  isDownloadDisabled,
  isDownloading,
  onPresentationDownload,
  onPreviewThemes,
  isPinned,
  onPinToggle,
}: IGetActionByName) => {
  const isHidden = slug === 'trash';

  switch (actionName) {
    case FolderManagerItemActionNames.EDIT:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.EDIT, payload: item });
          }}
        >
          Edit
        </DropdownItem>
      );

    case FolderManagerItemActionNames.EXPORT:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.EXPORT, payload: item });
          }}
        >
          Export
        </DropdownItem>
      );

    case FolderManagerItemActionNames.RENAME:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.RENAME, payload: item });
          }}
        >
          Rename
        </DropdownItem>
      );

    case FolderManagerItemActionNames.RESTORE:
      return isHidden ? (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.RESTORE, payload: item });
          }}
        >
          Restore
        </DropdownItem>
      ) : null;

    case FolderManagerItemActionNames.SHARE:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({
              actionType: FolderManagerActionTypes.SHARE,
              payload: { row: item, selectedItems: selectedItems },
            });
          }}
        >
          Share
        </DropdownItem>
      );

    case FolderManagerItemActionNames.DELETE: {
      const label = slug === 'trash' ? 'Delete' : 'Move to trash';
      return (
        <DropdownItem
          key={actionName}
          $danger
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.DELETE, payload: item });
          }}
        >
          {label}
        </DropdownItem>
      );
    }

    case FolderManagerItemActionNames.DOWNLOAD: {
      const isPresentationItem = item.type === FolderManagerItemTypes.PRESENTATION;
      const downloadLabel = isPresentationBuilding ? 'Building...' : isDownloading ? 'Downloading...' : 'Download';
      return (
        <DropdownItem
          key={actionName}
          disabled={isDownloadDisabled}
          onClick={(event) => {
            onClose(event);
            if (isPresentationItem && onPresentationDownload) {
              onPresentationDownload();
            } else {
              onAction({ actionType: FolderManagerActionTypes.DOWNLOAD, payload: item });
            }
          }}
        >
          {downloadLabel}
        </DropdownItem>
      );
    }

    case FolderManagerItemActionNames.DOWNLOAD_DECK:
      return (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.DOWNLOAD_DECK, payload: item });
          }}
        >
          Download Deck
        </DropdownItem>
      );

    case FolderManagerItemActionNames.SLIDE_DOWNLOAD:
      return (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.SLIDE_DOWNLOAD, payload: item });
          }}
        >
          Download Slide
        </DropdownItem>
      );

    case FolderManagerItemActionNames.UPLOAD:
      return (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.UPLOAD, payload: { pickFiles: true, item: item } });
          }}
        >
          Upload
        </DropdownItem>
      );

    case FolderManagerItemActionNames.MOVE:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.MOVE, payload: { item: item } });
          }}
        >
          Move
        </DropdownItem>
      );

    case FolderManagerItemActionNames.COPY:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.COPY, payload: item });
          }}
        >
          Copy
        </DropdownItem>
      );

    case FolderManagerItemActionNames.MANAGE_VERSIONS:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.MANAGE_VERSIONS, payload: item });
          }}
        >
          Manage Versions
        </DropdownItem>
      );

    case FolderManagerItemActionNames.ADD_TO_PRESENTATION:
      return (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({
              actionType: FolderManagerItemActionNames.ADD_TO_PRESENTATION,
              payload: { selectedItems: selectedItems?.length ? selectedItems : [item] },
            });
          }}
        >
          Add to Presentation
        </DropdownItem>
      );

    case FolderManagerItemActionNames.ADD_TO_CATEGORY:
      return (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({
              actionType: FolderManagerItemActionNames.ADD_TO_CATEGORY,
              payload: { selectedItems: selectedItems?.length ? selectedItems : [item] },
            });
          }}
        >
          Add to Category
        </DropdownItem>
      );

    case FolderManagerItemActionNames.REMOVE_FROM_PRESENTATION:
      return (
        <DropdownItem
          key={actionName}
          $danger
          onClick={(event) => {
            onClose(event);
            onAction({
              actionType: FolderManagerItemActionNames.REMOVE_FROM_PRESENTATION,
              payload: { selectedItems: selectedItems?.length ? selectedItems : [item] },
            });
          }}
        >
          Remove from Presentation
        </DropdownItem>
      );

    case FolderManagerItemActionNames.MANAGE_THEME:
      return (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.MANAGE_THEME, payload: item });
          }}
        >
          Manage Theme
        </DropdownItem>
      );

    case FolderManagerItemActionNames.CLONE:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.CLONE, payload: item });
          }}
        >
          Clone
        </DropdownItem>
      );

    case FolderManagerItemActionNames.SET_AS_DEFAULT:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.SET_AS_DEFAULT, payload: item });
          }}
        >
          Set as Default
        </DropdownItem>
      );

    case FolderManagerItemActionNames.PREVIEW_THEMES:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onPreviewThemes?.();
          }}
        >
          Preview Themes
        </DropdownItem>
      );

    case FolderManagerItemActionNames.REMOVE_PIN:
      return (
        <DropdownItem
          key={actionName}
          $danger
          onClick={(event) => {
            onClose(event);
            onPinToggle?.();
          }}
        >
          Remove
        </DropdownItem>
      );

    case FolderManagerItemActionNames.PIN:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onPinToggle?.();
          }}
        >
          {isPinned ? `Unpin ${getPinTypeLabel(item.type)}` : `Pin ${getPinTypeLabel(item.type)}`}
        </DropdownItem>
      );

    case FolderManagerItemActionNames.ADD_SUBFOLDER:
      return isHidden ? null : (
        <DropdownItem
          key={actionName}
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.NEW_FOLDER, payload: item });
          }}
        >
          Add Subfolder
        </DropdownItem>
      );

    case FolderManagerItemActionNames.REMOVE:
      return (
        <DropdownItem
          key={actionName}
          $danger
          onClick={(event) => {
            onClose(event);
            onAction({ actionType: FolderManagerActionTypes.DELETE, payload: item });
          }}
        >
          Remove
        </DropdownItem>
      );

    default:
      return null;
  }
};

export const FolderManagerItemActions: FC<IFolderManagerItemActions> = ({
  item,
  isLoading,
  selectedItems,
  actions,
  onAction,
  isMulti = false,
  triggerVariant,
}) => {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const { type: itemType } = item || {};
  const activeUserId = useSelector(authSelectors.getUserAuthState)?.user?.sub;
  const isPinned = useSelector((state: any) =>
    activeUserId ? pinnedItemsSelectors.isItemPinned(state, activeUserId, String(item.id)) : false,
  );

  const handlePinToggle = () => {
    if (!activeUserId) {
      dispatch(addNotification({ type: 'error', message: 'Unable to pin this item. Please sign in and try again.' }));
      return;
    }
    if (isPinned) {
      dispatch(unpinItem({ userId: activeUserId, itemId: String(item.id) }));
      dispatch(addNotification({ type: 'success', message: `${getPinTypeLabel(item.type)} unpinned.` }));
    } else {
      dispatch(
        pinItem({
          userId: activeUserId,
          item: {
            id: String(item.id),
            name: item.displayName || item.name || '',
            type: item.type || '',
          },
        }),
      );
      dispatch(addNotification({ type: 'success', message: `${getPinTypeLabel(item.type)} pinned to Home.` }));
    }
  };

  const isPresentation = itemType === FolderManagerItemTypes.PRESENTATION;
  const isSlide = itemType === FolderManagerMimeTypes.SLIDE;
  const isThemeSlide = slug === 'themes';
  const isDeleted = !!item.trashedAt;
  const [isThemePreviewOpen, setIsThemePreviewOpen] = useState(false);

  const mappedActions = useDeepCompareMemo(() => {
    let resolvedActions = actions;

    if (isThemeSlide) {
      resolvedActions = themeActions;
    }

    if (!isSlide || isMulti || !resolvedActions) {
      return resolvedActions;
    }

    if (resolvedActions.some((action) => action.name === FolderManagerItemActionNames.PREVIEW_THEMES)) {
      return resolvedActions;
    }

    const downloadIndex = resolvedActions.findIndex((action) => action.name === FolderManagerItemActionNames.DOWNLOAD);

    if (downloadIndex === -1) {
      return [
        ...resolvedActions,
        { name: FolderManagerItemActionNames.PREVIEW_THEMES, type: FolderManagerItemActionTypes.SINGLE },
      ];
    }

    return [
      ...resolvedActions.slice(0, downloadIndex + 1),
      { name: FolderManagerItemActionNames.PREVIEW_THEMES, type: FolderManagerItemActionTypes.SINGLE },
      ...resolvedActions.slice(downloadIndex + 1),
    ];
  }, [isThemeSlide, isSlide, isMulti, actions]);

  const { data: presentationData } = useGetPresentationQuery(
    { presentationId: item.id },
    { skip: !isPresentation || isDeleted },
  );

  const { isPresentationBuilding, buildData, stopBuildPolling } = usePresentationBuildPolling(
    isPresentation && !isDeleted ? item.id : undefined,
    {
      showNotifications: false,
      hasSlides: (presentationData?.slides?.length ?? 0) > 0,
    },
  );

  const { handleDownload: handlePresentationDownload, isDownloading } = usePresentationDownload({
    presentationId: item.id,
    displayName: item.displayName || item.name,
  });

  const hasNoSlides = presentationData && presentationData.slides?.length === 0;

  const isDownloadDisabled =
    isPresentation &&
    (isPresentationBuilding || isDownloading || !buildData || buildData.status !== 'completed' || hasNoSlides);

  const itemActions = item?.actions ? item.actions : getActionsForItemType(itemType as FolderManagerItemTypes);
  const isDisabled = (item as any)?.actionsDisabled === true;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDisabled) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation?.();
    setAnchorEl(null);
  };

  useEffect(() => {
    return () => {
      stopBuildPolling();
    };
  }, [stopBuildPolling]);

  if (actions !== undefined && actions.length === 0) {
    return null;
  }

  if (!actions?.length && !itemActions?.length) {
    return null;
  }

  return (
    <StyledActionsContainer>
      {isPresentation && isPresentationBuilding && (
        <CircularLoader icon={<SettingsOutlinedIcon />} size={20} centered spinIcon />
      )}
      {isPresentation && isDownloading && <CircularLoader size={20} centered />}

      <TriggerButton onClick={handleClick} disabled={isDisabled} $dark={triggerVariant === 'dark'} data-testid="MoreButton">
        <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </TriggerButton>

      <DropdownMenu
        item={item}
        slug={slug}
        actions={mappedActions}
        anchorEl={anchorEl}
        onAction={onAction}
        isLoading={isLoading}
        onClose={handleClose}
        open={Boolean(anchorEl)}
        itemActions={itemActions}
        selectedItems={selectedItems}
        isPresentationBuilding={isPresentationBuilding}
        isDownloadDisabled={isDownloadDisabled}
        isDownloading={isDownloading}
        onPresentationDownload={handlePresentationDownload}
        onPreviewThemes={() => setIsThemePreviewOpen(true)}
        isPinned={isPinned}
        onPinToggle={handlePinToggle}
      />

      {isSlide && (
        <SlideThemePreviewDialog
          item={item}
          open={isThemePreviewOpen}
          onClose={() => setIsThemePreviewOpen(false)}
        />
      )}
    </StyledActionsContainer>
  );
};
